import React, {
  FC,
  useEffect,
  useState,
  cloneElement,
  Fragment,
  ReactElement,
} from 'react';
import {
  List,
  TextField,
  EditButton,
  ShowButton,
  Filter,
  TextInput,
  TopToolbar,
  sanitizeListRestProps,
  useListContext,
  CreateButton,
  ExportButton,
  FilterProps,
  ListProps,
  PublicFieldProps,
  ToolbarProps,
  ExportButtonProps,
  InjectedFieldProps,
  SelectInput,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import Chip from '@material-ui/core/Chip';
import { UserInterface, getSupervisors } from 'services/regions';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { exporter } from 'providers/export';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_VIEW_REGION } from 'providers/permissions';
import { AppRole } from 'providers/roles';
import { Typography } from '@material-ui/core';
import { trackEvent } from 'utils/mixpanel';

const useStyles = makeStyles({
  rightMargin: {
    marginRight: '5px',
  },
});

const workflowSelectValues = [
  {
    text: 'Drywall',
    val: 'Drywall',
  },
  {
    text: 'Drywall and Paint',
    val: 'DrywallAndPaint',
  },
];

const RegionsFilter: FC<FilterProps> = props => (
  <Filter {...props}>
    <TextInput label="Name" source="name||$cont" data-cy="regionsFilterName" />
    <SelectInput
      source="phaseStructureType||$eq"
      label="Workflow"
      data-cy="phaseStructureType"
      choices={workflowSelectValues}
      emptyText="All"
      optionValue="val"
      optionText="text"
    />
  </Filter>
);

const Supervisors: FC<PublicFieldProps & InjectedFieldProps<UserInterface>> =
  props => {
    const classes = useStyles();

    const [regionalManagers, setRegionalManagers] = useState<UserInterface[]>(
      [],
    );

    const getUsers = async () => {
      if (!props.record) return;

      const users = await getSupervisors({
        regionId: props.record.id,
      });
      setRegionalManagers(users.data.data);
    };

    useEffect(() => {
      getUsers();
    }, []);

    return (
      <Box>
        {regionalManagers.map(regionalManager => (
          <Chip
            key={regionalManager.id}
            className={classes.rightMargin}
            label={`${regionalManager.firstName} ${regionalManager.lastName}`}
          />
        ))}
      </Box>
    );
  };

type ListActionProps = ToolbarProps &
  Pick<ExportButtonProps, 'maxResults'> & {
    filters?: ReactElement;
  };

const ListActions: FC<ListActionProps> = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    basePath,
    showFilter,
    total,
  } = useListContext();

  const { hasRole } = PermissionsProvider.useContainer();
  const canEdit = hasRole(AppRole.SuperAdmin);

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {canEdit ? <CreateButton basePath={basePath} /> : <Fragment />}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const WorkflowComponent = props => {
  return (
    <Typography>
      {props.record.phaseStructureType === 'DrywallAndPaint'
        ? 'Drywall and Paint'
        : 'Drywall'}
    </Typography>
  );
};

export const RegionsList: FC<ListProps> = props => {
  const { hasRole, hasPermission } = PermissionsProvider.useContainer();
  const canEdit = hasRole(AppRole.SuperAdmin);
  const canView = hasPermission(CAN_VIEW_REGION);

  useEffect(() => {
    trackEvent('PageView', {
      resource: 'regions',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);
  return (
    <List
      {...props}
      exporter={exporter('regions')}
      bulkActionButtons={<React.Fragment />}
      filters={<RegionsFilter>{props.filters}</RegionsFilter>}
      sort={{ field: 'name', order: 'ASC' }}
      actions={<ListActions />}
    >
      <Datagrid rowClick={() => (canEdit ? 'edit' : canView ? 'show' : '')}>
        <TextField source="name" />
        <Supervisors label="Manager" />
        <WorkflowComponent source="phaseStructureType" label="Workflow" />
        {canEdit ? <EditButton /> : canView ? <ShowButton /> : null}
      </Datagrid>
    </List>
  );
};
